import AiChatBackground from "@src/features/assistantChat/AiChatBackground";
import { Button } from "@src/lib/ui/Button";
import ChatComposer from "@src/features/aiAssist/uiComponents/ChatComposer";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import XIcon from "@src/lib/icons/12px/x";
import Text from "@src/lib/ui/Text";
import { useState } from "react";
import { ChatAttachmentItem } from "@src/features/aiAssist/uiComponents/ChatAttachment";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType } from "@src/types.generated";
import { useAiChatComposerFileUpload } from "@src/features/aiAssist/hooks/useAiChatComposerFileUpload";
import { ChatSidebarDragOverlay } from "@src/features/assistantChat/ChatSidebarDragOverlay";
import { ChatHistoryIconButton } from "@src/features/aiAssist/uiComponents/ChatHistoryIconButton";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dialogTitle: string;
  contentTitle: string;
  contentDescription: string;
  chatType: AiChatType;
  FooterButtons: React.ReactNode;
  onStartNewChat: () => void;
  placeholder?: string;
  initialInput?: string;
  enableChatHistory?: boolean;
  onSelectHistoricalChat?: (chat: AiChatFragment) => void;
}

const NewLibraryItemDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  dialogTitle,
  contentTitle,
  contentDescription,
  chatType,
  FooterButtons,
  onStartNewChat,
  placeholder = "Describe your content...",
  initialInput,
  enableChatHistory = false,
  onSelectHistoricalChat,
}) => {
  const [input, setInput] = useState(initialInput ?? "");
  const [attachments, setAttachments] = useState<ChatAttachmentItem[]>([]);
  const { submitMessage } = useChatContext();
  const { startNewConversation } = useChatSidebar(chatType);

  // Set up drag and drop for the entire dialog
  const { HiddenFileInput, dropzoneProps } = useAiChatComposerFileUpload({
    onAttachmentsChanged: setAttachments,
    enableDragAndDrop: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      startNewConversation();

      submitMessage({
        role: "user",
        content: input,
        customMessageData: {
          attachments,
        },
      });

      onStartNewChat();
      setInput("");
      setAttachments([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-w-[600px] gap-0 overflow-hidden p-0"
        hideCloseButton
      >
        {HiddenFileInput}
        <div className="flex h-[44px] items-center justify-between border-b p-3 pr-2">
          <Text variant="compact-title">{dialogTitle}</Text>
          <div className="flex items-center gap-2.5">
            {enableChatHistory && onSelectHistoricalChat && (
              <ChatHistoryIconButton
                filters={{ chatType }}
                onClickPreviousChat={onSelectHistoricalChat}
                variant="ghost"
                tooltip="View chat history"
                className="text-muted-foreground"
              />
            )}
            <Button
              variant="secondary"
              size="icon-xs"
              className="text-muted-foreground shadow-none"
              onClick={() => onOpenChange(false)}
            >
              <XIcon />
            </Button>
          </div>
        </div>
        <div
          className="relative flex flex-col gap-2.5 overflow-hidden p-8"
          {...(dropzoneProps?.getRootProps() || {})}
        >
          <AiChatBackground />
          <Text
            variant="body-title"
            className="text-center"
            data-testid="ai-course-creation-title"
          >
            {contentTitle}
          </Text>
          <Text
            variant="compact-description"
            className="mb-4 px-4 text-center text-muted-foreground"
            multiline
          >
            {contentDescription}
          </Text>
          <ChatComposer
            input={input}
            onInputChange={(e) => setInput(e.target.value)}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            className="min-h-[140px] placeholder:text-gray-5"
            attachmentProps={{
              enableAttachments: true,
              attachments,
              onAttachmentsChanged: setAttachments,
            }}
            autoFocus={true}
          />
          {dropzoneProps && (
            <ChatSidebarDragOverlay isDragActive={dropzoneProps.isDragActive} />
          )}
          <Text
            variant="compact-description"
            className="mt-2 text-center text-muted-foreground"
          >
            Need help?{" "}
            <a
              href="https://help.opus.so/en/articles/12002784-how-to-build-training-using-ai-assist"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline decoration-dotted"
            >
              Read the guide
            </a>
          </Text>
        </div>
        <div className="flex flex-col items-center justify-center border-t border-border bg-muted p-3">
          <Text
            variant="compact-description"
            className="text-center text-muted-foreground"
          >
            or
          </Text>
          <div className="mt-3 flex items-center gap-5 self-stretch">
            {FooterButtons}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewLibraryItemDialog;
